'use client';

import Link from '@eliadmin/core/src/components/link';

import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Carousel } from '../ui/carousel';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { GoArrowUpRight } from 'react-icons/go';
import { useState, useEffect } from 'react';
import { useCurrentStore } from '../../utils/use-store-utils';

interface DesktopCarouselProps {
  className?: {
    divContainer?: string;
    main?: string;
    wrapper?: string;
    container?: string;
    slide?: string;
  };
  slides: React.ReactNode[];
  inForm?: boolean;
  nextButton?: React.ReactNode;
  landingPageButton?: boolean;
  buttonHref?: string;
  /** Render one responsive slide tree instead of separate CSS-hidden carousels. */
  responsive?: boolean;
}

export function DesktopCarousel({
  slides,
  className,
  inForm,
  nextButton,
  landingPageButton = false,
  buttonHref,
  responsive = false,
}: DesktopCarouselProps) {
  const { isGasGas, isCFMoto } = useCurrentStore();
  const { t } = useTranslations();

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;
  // The provider is populated after mount. Use SSR data for stable initial controls.
  const slideCount = slides.length;

  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1290px)');
    setIsDesktop(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  let adjustedIndex = selectedIndex;

  if (slides.length === 1) {
    adjustedIndex = selectedIndex;
  } else if (slides.length === 2 && isDesktop) {
    adjustedIndex = selectedIndex + 1;
  } else if (slides.length > 2 && isDesktop) {
    adjustedIndex = selectedIndex + 2;
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-12',
        className?.divContainer,
      )}
    >
      <Carousel
        mobileCarouselDots={true}
        slides={slides}
        className={{
          main: cn('h-full w-full', className?.main),
          wrapper: cn('h-full w-full', className?.wrapper),
          container: cn('h-full w-full gap-6', className?.container),
          slide: cn(className?.slide),
        }}
      />
      <div
        className={cn(
          'flex w-full items-center justify-between gap-6',
          responsive && 'px-6 md:px-0',
        )}
      >
        <div className={cn('shrink-0', responsive && 'hidden md:block')}>
          <SwitchCounter selectedIndex={adjustedIndex} slideCount={slideCount} />
        </div>
        <div className={cn('w-full', responsive && 'hidden md:block')}>
          <ProgressBar selectedIndex={adjustedIndex} slideCount={slideCount} />
        </div>
        {inForm ? (
          nextButton
        ) : (
          <>
            {landingPageButton ? (
              <Button
                asChild
                className={cn(
                  'whitespace-nowrap',
                  responsive && 'w-full md:w-auto',
                  isGasGas && '!text-white',
                  isCFMoto && '!text-white',
                )}
              >
                <Link prefetch={false} href={buttonHref ?? 'https://rsmoto.sk'} target="_blank">
                  {t('text3319')} <GoArrowUpRight size={18} />
                </Link>
              </Button>
            ) : (
              <Button
                asChild
                variant={'dark'}
                className={cn(
                  'whitespace-nowrap',
                  isGasGas && '!text-white',
                  isCFMoto && '!text-white',
                )}
              >
                <Link prefetch={false} href={ROUTES.CATEGORIES()}>
                  {t('text2014')} <GoArrowUpRight size={18} />
                </Link>
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SwitchCounter({
  selectedIndex,
  slideCount,
}: {
  selectedIndex: number;
  slideCount: number;
}) {
  return (
    <div className="flex items-center justify-start gap-6">
      <CarouselControls
        prevIcon={<LuChevronLeft size={20} className="text-[#B2B2B2]" />}
        className={{
          main: cn('ea-desktop-carousel__controls-first'),
          button:
            'flex min-h-14 min-w-14 items-center justify-center bg-black/10 disabled:opacity-50',
        }}
      />
      <p className="whitespace-nowrap text-3xl font-normal tracking-tighter text-black">
        0{selectedIndex + 1}{' '}
        <span className="whitespace-nowrap text-lg font-normal text-[#B2B2B2]">
          / 0{slideCount}
        </span>
      </p>
      <CarouselControls
        nextIcon={<LuChevronRight size={20} className="text-[#B2B2B2]" />}
        className={{
          main: cn('ea-desktop-carousel__controls-second'),
          button:
            'flex min-h-14 min-w-14 items-center justify-center bg-black/10 disabled:opacity-50',
        }}
      />
    </div>
  );
}

function ProgressBar({ selectedIndex, slideCount }: { selectedIndex: number; slideCount: number }) {
  const progressWidth =
    slideCount > 0 ? Math.min(100, ((selectedIndex + 1) / slideCount) * 100) : 0;

  return (
    <div className="relative h-1 w-full bg-[#F5F5F5]">
      <div className="absolute left-0 top-0 h-1 bg-black" style={{ width: `${progressWidth}%` }} />
    </div>
  );
}
